import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as cognito from './cognito';

/** The signed-in creator, as much of them as the app chrome ever needs. */
export interface AuthSession {
  email: string;
  /** Cognito sub; matches `Profile.userId` on everything the API returns. */
  sub: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  session: AuthSession | null;
  /**
   * Three-state on purpose. A boolean would make "not signed in" and "not
   * checked yet" the same value, and the router would bounce a signed-in
   * creator to /login for the tick before the stored session resolves.
   */
  status: AuthStatus;
  /** Convenience view of `status === 'loading'`. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthSession>;
  signOut: () => void;
  /** Re-reads the stored session, refreshing tokens if they have expired. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<AuthSession | null>(null);

  const apply = useCallback((info: cognito.SessionInfo | null) => {
    setSession(info ? { email: info.email, sub: info.sub } : null);
    setStatus(info ? 'authenticated' : 'unauthenticated');
  }, []);

  const refresh = useCallback(async () => {
    apply(await cognito.currentSession());
  }, [apply]);

  // Restore on mount. `active` matters under StrictMode, which mounts twice:
  // the first pass must not resolve into an unmounted tree.
  useEffect(() => {
    let active = true;
    cognito.currentSession().then(
      (info) => {
        if (active) apply(info);
      },
      () => {
        // A refresh token that has expired or been revoked is a normal signed
        // out state, not a boot failure.
        if (active) apply(null);
      },
    );
    return () => {
      active = false;
    };
  }, [apply]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      status,
      loading: status === 'loading',
      refresh,
      signIn: async (email, password) => {
        const info = await cognito.signIn(email, password);
        const next: AuthSession = { email: info.email, sub: info.sub };
        setSession(next);
        setStatus('authenticated');
        return next;
      },
      signOut: () => {
        cognito.signOut();
        apply(null);
      },
    }),
    [apply, refresh, session, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
